import gsap from "gsap";
import { DrawSVGPlugin, ScrollTrigger, ScrollSmoother } from "gsap/all";
import DefaultBlock from "/scripts/default/block";

// Register Plugins
gsap.registerPlugin(DrawSVGPlugin, ScrollTrigger, ScrollSmoother);

export default class ScrollTriggerSize extends DefaultBlock {
	// Define variables
	init() {
		const selector = gsap.utils.selector(this.block);

		const DOM = {
			block: this.block,
			reel: selector("#size-text-reel"),
			textLeft: selector("#size-text-left"),
			textRight: selector("#size-text-right"),
			dot: selector("#size-dot"),
			deviceWrap: selector("#size-device-wrap"),
			device: selector("#size-device"),
		};
		this.DOM = DOM;

		this.defaults = {
			ease: "back.out(1.1)",
			duration: 0.8,
		};

		this.createTimeline();
	}

	wideAnimation() {
		const tl = gsap.timeline({
			id: "scroll-size",
			defaults: this.defaults,
			repeat: -1,
			repeatDelay: 1,
			repeatRefresh: true,
		});

		gsap.set(this.DOM.reel, { autoAlpha: 1 });

		tl.set(this.DOM.device, {
			height: "90%",
			width: "100%",
		});

		tl.addLabel("toMobile");

		tl.to(
			this.DOM.device,
			{
				height: "100%",
				width: "30%",
			},
			"toMobile"
		);

		tl.to(
			this.DOM.dot,
			{
				scale: 0.6,
			},
			"toMobile"
		);

		tl.to(
			this.DOM.textLeft,
			{
				scale: 1.3,
				x: () => {
					return this.DOM.deviceWrap[0].getBoundingClientRect().width * 0.35;
				},
			},
			"toMobile"
		);

		tl.to(
			this.DOM.textRight,
			{
				scale: 1.3,
				x: () => {
					return this.DOM.deviceWrap[0].getBoundingClientRect().width * -0.35;
				},
			},
			"toMobile"
		);

		tl.addLabel("toTablet", "+=1");

		tl.to(
			this.DOM.device,
			{
				height: "50%",
				width: "60%",
			},
			"toTablet"
		);

		tl.to(
			this.DOM.dot,
			{
				scale: 0.3,
			},
			"toTablet"
		);

		tl.to(
			this.DOM.textLeft,
			{
				x: () => {
					return this.DOM.deviceWrap[0].getBoundingClientRect().width * 0.2;
				},
			},
			"toTablet"
		);

		tl.to(
			this.DOM.textRight,
			{
				x: () => {
					return this.DOM.deviceWrap[0].getBoundingClientRect().width * -0.2;
				},
			},
			"toTablet"
		);

		tl.addLabel("toDesktop", "+=1");

		tl.to(
			this.DOM.device,
			{
				height: "90%",
				width: "100%",
			},
			"toDesktop"
		);

		tl.to(
			this.DOM.dot,
			{
				scale: 1,
			},
			"toDesktop"
		);

		tl.to(
			this.DOM.textLeft,
			{
				x: 0,
				scale: 1,
			},
			"toDesktop"
		);

		tl.to(
			this.DOM.textRight,
			{
				x: 0,
				scale: 1,
			},
			"toDesktop"
		);

		return tl;
	}

	tallAnimation() {
		const tl = gsap.timeline({
			id: "scroll-size",
			defaults: this.defaults,
			repeat: -1,
			repeatDelay: 1,
			repeatRefresh: true,
		});

		gsap.set(this.DOM.reel, { autoAlpha: 1 });

		tl.set(this.DOM.device, {
			height: "90%",
			width: "100%",
		});

		tl.addLabel("toMobile");

		tl.to(
			this.DOM.device,
			{
				height: "100%",
				width: "30%",
			},
			"toMobile"
		);

		tl.to(
			this.DOM.dot,
			{
				scale: 0.6,
			},
			"toMobile"
		);

		tl.to(
			this.DOM.textLeft,
			{
				scale: 1,
				y: 0,
			},
			"toMobile"
		);

		tl.to(
			this.DOM.textRight,
			{
				scale: 1,
				y: 0,
			},
			"toMobile"
		);

		tl.addLabel("toTablet", "+=1");

		tl.to(
			this.DOM.device,
			{
				height: "50%",
				width: "60%",
			},
			"toTablet"
		);

		tl.to(
			this.DOM.dot,
			{
				scale: 0.3,
			},
			"toTablet"
		);

		tl.to(
			this.DOM.textLeft,
			{
				scale: 1.2,
				y: () => {
					return this.DOM.deviceWrap[0].getBoundingClientRect().height * 0.25;
				},
			},
			"toTablet"
		);

		tl.to(
			this.DOM.textRight,
			{
				scale: 1.2,
				y: () => {
					return this.DOM.deviceWrap[0].getBoundingClientRect().height * -0.25;
				},
			},
			"toTablet"
		);

		tl.addLabel("toDesktop", "+=1");

		tl.to(
			this.DOM.device,
			{
				height: "90%",
				width: "100%",
			},
			"toDesktop"
		);

		tl.to(
			this.DOM.dot,
			{
				scale: 1,
			},
			"toDesktop"
		);

		tl.to(
			this.DOM.textLeft,
			{
				y: () => {
					return this.DOM.deviceWrap[0].getBoundingClientRect().height * 0.05;
				},
				scale: 0.8,
			},
			"toDesktop"
		);

		tl.to(
			this.DOM.textRight,
			{
				y: () => {
					return this.DOM.deviceWrap[0].getBoundingClientRect().height * -0.05;
				},
				scale: 0.8,
			},
			"toDesktop"
		);

		return tl;
	}

	createTimeline() {
		const mm = gsap.matchMedia();
		let timeline;

		mm.add(
			{
				isDesktop: "(min-width: 1240px)",
				dontReduceMotion: "(prefers-reduced-motion: no-preference)",
			},
			(context) => {
				let { isDesktop, dontReduceMotion } = context.conditions;

				if (!dontReduceMotion) return;

				/* Animate */
				if (isDesktop) {
					timeline = this.wideAnimation();
				} else {
					timeline = this.tallAnimation();
				}

				return () => {
					timeline && timeline.kill();
					gsap.set(this.DOM.textLeft, { clearProps: "all" });
					gsap.set(this.DOM.textRight, { clearProps: "all" });
				};
			}
		);
	}
}
